import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import Header from "../Components/Header";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import api from '../api';
import {
  Check,
  ArrowRight,
  BarChart2,
  Zap,
  Shield,
  Users,
  Clock,
  ChevronDown,
  Star
} from "lucide-react";
import { FiPlus, FiMinus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer";
import SimpleKakaoButton from "../Components/SimpleKakaoButton";

const Pricing = () => {
  const navigate = useNavigate();
  const baseFee = 100000; // 기본 요금 10만원
  const baseTraffic = 20000; // 기본 포함 MTU 2만명
  const additionalCostPerTraffic = 1.5; // 추가 MTU 1명당 1.5원

  const [activeTab, setActiveTab] = useState("monthly");
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  const plans = [
    {
      name: "무료 체험",
      description: "30일 동안 무료로 체험해보세요",
      type: "free",
      maxTraffic: 10000,
      badge: "추천",
      tagline: "쉬운 시작",
      features: [
        { text: "URL A/B 테스트", status: "available" },
        { text: "비주얼 A/B 테스트", status: "available" },
        { text: "히트맵 분석", status: "available" },
        { text: "퍼널 분석", status: "available" },
        { text: "팝업 · 배너", status: "available" },
        { text: "세션 녹화", status: "available" },
        { text: "프로젝트 최대 1개", status: "available" },
        { text: "2개월 데이터 보관", status: "available" },
        { text: "실시간 대시보드", status: "available" },
        { text: "기본 가이드 문서", status: "available" },
        { text: "이메일 기술 지원", status: "available" }
      ]
    },
    {
      name: "Standard",
      description: "성장하는 기업을 위한 확장된 기능 (30일)",
      type: "paid",
      minTraffic: 20000,
      maxTraffic: 990000,
      badge: "",
      tagline: "가장 인기",
      features: [
        { text: "URL A/B 테스트", status: "available" },
        { text: "비주얼 A/B 테스트", status: "available" },
        { text: "히트맵 분석", status: "available" },
        { text: "퍼널 분석", status: "available" },
        { text: "팝업 · 배너", status: "available" },
        { text: "세션 녹화", status: "available" },
        { text: "프로젝트 최대 10개", status: "available" },
        { text: "6개월 데이터 보관", status: "available" },
        { text: "실시간 대시보드", status: "available" },
        { text: "우선 기술 지원", status: "available" },
        { text: `기본 ${formatTraffic(baseTraffic)} 포함`, status: "available" },
        { text: "기간별 리포트", status: "available" },
        { text: "일자별 리포트", status: "available" },
      ]
    },
    {
      name: "Enterprise",
      description: "대규모 기업을 위한 맞춤형 솔루션 (30일)",
      type: "paid",
      minTraffic: 1000000,
      maxTraffic: 5000000,
      badge: "프리미엄",
      tagline: "확장성",
      features: [
        { text: "URL A/B 테스트", status: "available" },
        { text: "비주얼 A/B 테스트", status: "available" },
        { text: "히트맵 분석", status: "available" },
        { text: "퍼널 분석", status: "available" },
        { text: "팝업 · 배너", status: "available" },
        { text: "세션 녹화", status: "available" },
        { text: "프로젝트 개수 무제한", status: "available" },
        { text: "12개월 데이터 보관", status: "available" },
        { text: "실시간 대시보드", status: "available" },
        { text: "전담 계정 매니저", status: "available" },
        { text: `기본 ${formatTraffic(baseTraffic)} 포함`, status: "available" },
        { text: "온보딩 교육 지원", status: "available" },
        { text: "기간별 리포트", status: "available" },
        { text: "일자별 리포트", status: "available" },
      ]
    }
  ];

  // 임시로 무료 체험 플랜을 제외한 플랜만 표시하는 변수 추가
  const visiblePlans = plans.filter(plan => plan.type !== "free");

  const [planTraffic, setPlanTraffic] = useState({
    standard: 20000,
    enterprise: 1000000
  });

  const handleTrialSignup = async () => {
    try {
      const response = await api.post('payments/trial/');
      const WorkspaceCode = response.data.workspace;

      if (WorkspaceCode) {
        window.location.href = `${process.env.REACT_APP_APP_DOMAIN}/projects/${WorkspaceCode}/overview`
      } else {
        window.location.href = `${process.env.REACT_APP_APP_DOMAIN}/workspaces`
      }
    } catch (error) {
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      }
      console.error('Trial signup failed:', error);
    }
  };

  const handleTrafficChange = (plan, e) => {
    setPlanTraffic(prev => ({
      ...prev,
      [plan.toLowerCase()]: Number(e.target.value)
    }));
  };

  // 10,000명 단위로 트래픽 증감
  const handleTrafficStep = (plan, increment) => {
    const planKey = plan.toLowerCase();
    const planObj = plans.find(p => p.name.toLowerCase() === planKey);

    setPlanTraffic(prev => {
      let newTraffic = prev[planKey];

      if (increment) {
        // 10,000명 증가
        newTraffic += 10000;
        // 최대값 체크
        if (newTraffic > planObj.maxTraffic) {
          newTraffic = planObj.maxTraffic;
        }
      } else {
        // 10,000명 감소
        newTraffic -= 10000;
        // 최소값 체크
        if (newTraffic < planObj.minTraffic) {
          newTraffic = planObj.minTraffic;
        }
      }

      return {
        ...prev,
        [planKey]: newTraffic
      };
    });
  };

  const getPlanCode = (planName) => {
    switch (planName.toLowerCase()) {
      case "무료 체험":
        return "F";
      case "standard":
        return "S";
      case "enterprise":
        return "E";
      default:
        return "S";
    }
  };

  const handlePayment = async (plan, traffic) => {
    let customerKey = null;

    try {
      const data = {
        plan: getPlanCode(plan),
        traffic: traffic
      };

      const response = await api.post('payments/cards/request/', data);
      const customerKey = response.data.customer_key;
      const orderId = response.data.order_id;
      const WorkspaceCode = response.data.workspace;

      // 정기 결제가 등록되어 있는 경우 공간 설정 페이지로 리다이렉트 
      if (WorkspaceCode) {
        window.location.href = `${process.env.REACT_APP_APP_DOMAIN}/projects/${WorkspaceCode}/overview`
      }

      const tossPayments = await loadTossPayments(process.env.REACT_APP_CLIENTKEY);
      const payment = tossPayments.payment({ customerKey });

      await payment.requestBillingAuth({
        method: "CARD",
        successUrl: `${window.location.origin}/success?order_id=${orderId}`,
        failUrl: `${window.location.origin}/fail?customer_key=${customerKey}`,
      }).catch(async error => {
        if (error.code === 'USER_CANCEL') {
          // 창이 닫혔을 때 recurring_payment를 삭제
          if (customerKey) {
            try {
              await api.delete('payments/cards/request/', {
                data: {
                  customer_key: customerKey
                }
              });
            } catch (cancelError) {
              console.error('정기 결제 상태 변경 실패:', cancelError);
            }
          }
          return;
        }
        alert(error.message || '카드 등록에 실패했습니다.');
      });
    } catch (error) {
      // API 요청 실패 시에도 pending 상태의 recurring_payment를 삭제
      if (customerKey) {
        try {
          await api.delete('payments/cards/request/', {
            customer_key: customerKey
          });
        } catch (cancelError) {
          console.error('정기 결제 상태 변경 실패:', cancelError);
        }
      }

      if (error.response?.data?.message) {
        alert(error.response.data.message);
      }
    }
  };

  // 방문자 수를 '만 명' 단위로 표시
  function formatTraffic(traffic) {
    return (traffic / 10000).toLocaleString() + "만명";
  }

  // 새로운 가격 계산 로직
  function calculateCost(traffic) {
    if (traffic <= baseTraffic) {
      // 기본 MTU 이하인 경우 기본 요금만 적용
      return baseFee.toLocaleString();
    } else {
      // 초과 트래픽에 대해 추가 요금 계산
      const additionalTraffic = traffic - baseTraffic;
      const additionalCost = additionalTraffic * additionalCostPerTraffic;
      const totalCost = baseFee + additionalCost;
      return totalCost.toLocaleString();
    }
  }

  // 기본 요금만 표시하는 함수 추가
  function formatBaseFee() {
    return baseFee.toLocaleString();
  }

  // 추가 트래픽 계산 함수 추가
  function calculateAdditionalTraffic(traffic) {
    if (traffic <= baseTraffic) return 0;
    return traffic - baseTraffic;
  }

  // 추가 요금 계산 함수 추가
  function calculateAdditionalCost(traffic) {
    const additionalTraffic = calculateAdditionalTraffic(traffic);
    return (additionalTraffic * additionalCostPerTraffic).toLocaleString();
  }

  const handleAction = (plan, traffic) => {
    if (plan.type === "free") {
      handleTrialSignup();
    } else if (traffic >= 5000000) {
      navigate('/contact');
    } else {
      handlePayment(plan.name, traffic);
    }
  };

  // FAQ 데이터
  const faqs = [
    {
      question: "테스트별 참여자 수란 무엇인가요?",
      answer: "쇼핑몰 전체 방문자 수가 아닌, 실제로 각 A/B 테스트·히트맵·퍼널·팝업·세션 녹화에 참여한 사람의 누적 수를 의미합니다. 한 명이 동일한 항목에 여러 번 접속해도 1명으로 계산됩니다. 서로 다른 2개의 항목에 각각 참여할 경우 2명으로 누적 계산됩니다."
    },
    {
      question: "결제는 언제 이루어지나요?",
      answer: "첫 결제는 서비스 등록 시 바로 이루어지며, 이후 매월 같은 날짜에 자동으로 결제됩니다. 결제 내역은 대시보드에서 확인 가능합니다."
    },
    {
      question: "설정한 참여자 수를 초과하면 어떻게 되나요?",
      answer: "설정한 참여자 수를 모두 소진하면 서비스가 일시 중단됩니다. 단, '초과 방문자 자동 결제'를 켜두시면 중단 없이 계속 이용하실 수 있으며, 초과 비용(1명당 ₩1.5)은 다음 정기 결제일에 함께 청구됩니다. 대시보드에서 현재 사용량을 확인하고 필요에 따라 플랜을 업그레이드하실 수 있습니다."
    },

    {
      question: "언제든지 요금제를 변경할 수 있나요?",
      answer: "네, 언제든지 요금제를 변경할 수 있습니다. 업그레이드의 경우 즉시 적용되어 바로 새로운 기능과 혜택을 이용하실 수 있습니다. 다운그레이드는 다음 결제 주기부터 적용됩니다."
    },
    {
      question: "환불 정책은 어떻게 되나요?",
      answer: "결제 후 7일 이내에만 환불이 가능하며, 실험 참여자 수를 1명이라도 사용하신 경우 환불이 불가합니다. 자세한 내용은 이용 약관을 참조해 주세요."
    }
  ];

  // 선택된 FAQ 토글
  const toggleFaq = (index) => {
    if (openFaq === index) {
      setOpenFaq(null);
    } else {
      setOpenFaq(index);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 플로팅 CTA 버튼 (스크롤 시 표시) */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <Link to="/contact">
          <button className="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
            <span className="font-medium">문의하기</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </div>

      {/* 히어로 섹션 - 배경 그라데이션 추가 */}
      <section className="pt-32 pb-16 relative overflow-hidden bg-gradient-to-b from-gray-50 via-white to-white">
        {/* 배경 장식 요소 */}
        <div className="absolute top-0 right-0 opacity-20 -z-10">
          <svg width="600" height="600" viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="300" cy="300" r="300" fill="url(#blue-radial)" />
            <defs>
              <radialGradient id="blue-radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(300 300) rotate(90) scale(300)">
                <stop stopColor="#3B82F6" />
                <stop offset="1" stopColor="#3B82F6" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 프로모션 배너 */}
          <div className="text-center mb-10">
            <div className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold px-4 py-1 rounded-full mb-4">
              🎉 오픈 이벤트 특별 가격
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              성장하는 기업을 위한 <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">전환율 최적화 솔루션</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              데이터 기반 의사결정으로 매출을 증대하세요. 단 한 줄의 코드로 시작할 수 있습니다.
            </p>
          </div>

          {/* 가격 요약 */}
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto mb-16 border border-gray-100 transform hover:translate-y-[-5px] transition-all duration-300">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  기본 요금 <span className="text-blue-600">₩100,000</span>
                </h2>
                <p className="text-lg text-gray-600 mb-2">
                  기본 2만 명 포함 <span className="text-sm">(테스트 참여자 기준)</span>
                </p>
                <p className="text-gray-600">
                  추가 참여자 1명당 <span className="font-medium">₩1.5</span>
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="ml-3 text-gray-600">간편한 설정으로 5분 만에 시작</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <BarChart2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="ml-3 text-gray-600">실시간 데이터 분석 및 리포트</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="ml-3 text-gray-600">무료로 30일 체험 가능 (문의 필요)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 결제 주기 선택 탭 (준비는 해두되 비활성화) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex justify-center">
          <div className="inline-flex rounded-lg p-1 bg-gray-100">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === "monthly"
                ? "bg-white shadow-sm text-gray-900"
                : "text-gray-500 hover:text-gray-900"
                }`}
              onClick={() => setActiveTab("monthly")}
            >
              월간 요금제
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === "annual"
                ? "bg-white shadow-sm text-gray-900"
                : "text-gray-500 hover:text-gray-900"
                }`}
              disabled
              title="준비 중입니다"
            >
              연간 요금제 (준비 중)
            </button>
          </div>
        </div>
      </div>

      {/* 요금제 카드 - 새 디자인 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="grid md:grid-cols-3 gap-8">
          {visiblePlans.map((plan, index) => (
            <div
              key={plan.name}
              className={`bg-white rounded-xl overflow-hidden group transition-all duration-300
                ${index === 1 ? 'border-2 border-blue-500 shadow-xl hover:shadow-2xl transform hover:-translate-y-2' : 'border border-gray-200 shadow-md hover:shadow-xl hover:-translate-y-1'}`}
            >
              {/* 플랜 헤더 */}
              <div className={`px-6 py-4 ${index === 1 ? 'bg-blue-50' : 'bg-gray-50'}`}>
                {plan.badge && (
                  <div className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mb-2
                    ${index === 1 ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-800'}`}>
                    {plan.badge}
                  </div>
                )}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                  </div>
                  {index === 1 && (
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center">
                      <Star className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* 가격 정보 */}
              <div className="px-6 pt-6 pb-3 border-b border-gray-100">
                <div className="space-y-2">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900">
                    {planTraffic[plan.name.toLowerCase()] >= 5000000
                      ? "가격 문의 필요"
                      : `₩${calculateCost(planTraffic[plan.name.toLowerCase()])} /월`}
                  </div>
                  <div className="text-sm text-gray-500">(*VAT 별도)</div>
                  {planTraffic[plan.name.toLowerCase()] > baseTraffic && (
                    <div className="text-sm text-gray-600 pt-1">
                      기본 ₩{formatBaseFee()} + 추가 ₩{calculateAdditionalCost(planTraffic[plan.name.toLowerCase()])}
                    </div>
                  )}
                </div>
              </div>

              {/* MTU 슬라이더 */}
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">월간 테스트별 참여자 수</span>
                    <span className="text-sm font-medium text-blue-600">
                      {formatTraffic(planTraffic[plan.name.toLowerCase()])}
                      {planTraffic[plan.name.toLowerCase()] >= 5000000 && '+'}
                    </span>
                  </div>

                  <div className="text-center mb-3">
                    <p className="text-xs text-gray-500">10,000명 단위로 조정 가능</p>
                  </div>

                  <div className="flex items-center justify-center gap-3">
                    <button
                      className="p-1.5 rounded-full border border-gray-300 hover:bg-gray-100 disabled:opacity-50 flex-shrink-0"
                      onClick={() => handleTrafficStep(plan.name, false)}
                      disabled={planTraffic[plan.name.toLowerCase()] <= plan.minTraffic}
                    >
                      <FiMinus className="w-3 h-3" />
                    </button>

                    <div className="relative w-full h-10">
                      <div className="absolute w-full top-1/2 transform -translate-y-1/2">
                        <div className="relative h-2 flex items-center">
                          {/* 배경 트랙 */}
                          <div className="absolute w-full h-full bg-gray-200 rounded-lg"></div>
                          {/* 진행 바 */}
                          <div
                            className={`absolute h-full rounded-lg ${index === 1 ? 'bg-blue-600' : 'bg-indigo-600'}`}
                            style={{
                              width: `${((planTraffic[plan.name.toLowerCase()] - plan.minTraffic) / (plan.maxTraffic - plan.minTraffic)) * 100}%`
                            }}
                          ></div>
                        </div>

                        {/* range input과 핸들 */}
                        <div className="absolute w-full top-0 -mt-1">
                          <input
                            type="range"
                            min={plan.minTraffic}
                            max={plan.maxTraffic}
                            step={10000}
                            value={planTraffic[plan.name.toLowerCase()]}
                            onChange={(e) => handleTrafficChange(plan.name, e)}
                            className="absolute w-full h-4 opacity-0 cursor-pointer z-10"
                            style={{
                              height: '24px',
                              width: 'calc(100% + 16px)',
                              left: '-8px',
                              top: '-4px'
                            }}
                          />
                          {/* 동그라미 핸들 */}
                          <div
                            className={`absolute w-4 h-4 rounded-full shadow-md pointer-events-none ${index === 1 ? 'bg-blue-600' : 'bg-indigo-600'}`}
                            style={{
                              left: `${((planTraffic[plan.name.toLowerCase()] - plan.minTraffic) / (plan.maxTraffic - plan.minTraffic)) * 100}%`,
                              transform: 'translateX(-50%)',
                              top: '0'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <button
                      className="p-1.5 rounded-full border border-gray-300 hover:bg-gray-100 disabled:opacity-50 flex-shrink-0"
                      onClick={() => handleTrafficStep(plan.name, true)}
                      disabled={planTraffic[plan.name.toLowerCase()] >= plan.maxTraffic}
                    >
                      <FiPlus className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{formatTraffic(plan.minTraffic)}</span>
                    <span>{formatTraffic(plan.maxTraffic)}{plan.maxTraffic >= 5000000 && '+'}</span>
                  </div>
                </div>

                {/* 추가된 MTU 설명 */}
                {planTraffic[plan.name.toLowerCase()] > baseTraffic && (
                  <div className="mt-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <div className="font-medium mb-1">상세 비용 안내:</div>
                    <div className="flex justify-between mb-1">
                      <span>기본 요금 ({formatTraffic(baseTraffic)} 포함)</span>
                      <span>₩{formatBaseFee()}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span>추가 참여자 ({formatTraffic(calculateAdditionalTraffic(planTraffic[plan.name.toLowerCase()]))})</span>
                      <span>₩{calculateAdditionalCost(planTraffic[plan.name.toLowerCase()])}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-blue-100 font-medium">
                      <span>총 비용</span>
                      <span>₩{calculateCost(planTraffic[plan.name.toLowerCase()])}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 기능 목록 */}
              <div className="px-6 py-6">
                <h4 className="font-medium text-gray-900 mb-4">제공 기능</h4>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      {feature.status === "available" ? (
                        <Check className={`h-5 w-5 mr-2 flex-shrink-0 ${index === 1 ? 'text-blue-600' : 'text-indigo-600'}`} />
                      ) : (
                        <div className="flex-shrink-0 mr-2 mt-0.5">
                          <div className="inline-block px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                            {feature.label}
                          </div>
                        </div>
                      )}
                      <span className="text-gray-700 text-sm">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                {/* 액션 버튼 */}
                <button
                  className={`w-full px-6 py-3 rounded-lg font-medium transition-colors text-white
                  ${index === 1
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md'
                      : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800'}`}
                  onClick={() => handleAction(plan, planTraffic[plan.name.toLowerCase()])}
                >
                  {planTraffic[plan.name.toLowerCase()] >= 5000000 ? "문의하기" : "서비스 시작하기"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 비교표 - 비주얼 편집 테스트 추가, AI 인사이트 추천 제거 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">플랜 상세 비교</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            각 요금제에 포함된 세부 기능을 비교해보세요
          </p>
        </div>

        {/* 모바일 스크롤 힌트 */}
        <div className="lg:hidden mb-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
            <span>👉</span>
            <span>좌우로 스크롤하여 비교하세요</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          {/* 스크롤 가능한 컨테이너 */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-gray-500 font-medium text-xs sm:text-sm sticky left-0 bg-gray-50 z-10">
                    기능
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-500 font-medium text-xs sm:text-sm whitespace-nowrap">
                    무료 체험
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-blue-600 font-medium text-xs sm:text-sm whitespace-nowrap">
                    Standard
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-indigo-600 font-medium text-xs sm:text-sm whitespace-nowrap">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    URL A/B 테스트
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    비주얼 A/B 테스트
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    히트맵 분석
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    퍼널 분석
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    팝업 · 배너
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    세션 녹화
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    실시간 대시보드
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    기간별 리포트
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    일자별 리포트
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    프로젝트 수
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    1개
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    10개
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    무제한
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    데이터 보관 기간
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    2개월
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    6개월
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    12개월
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    기술 지원
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    이메일
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    우선 지원
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    전담 매니저
                  </td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-800 font-medium sticky left-0 bg-white z-10">
                    온보딩 교육 지원
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-400">-</td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-400">-</td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" />
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 자주 묻는 질문 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">자주 묻는 질문</h2>
          <p className="text-lg text-gray-600">
            궁금한 점이 있으신가요? 아래에서 답변을 찾아보세요
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center"
                onClick={() => toggleFaq(index)}
              >
                <h3 className="text-base md:text-lg font-medium text-gray-900">{faq.question}</h3>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'transform rotate-180' : ''}`} />
              </button>

              <div className={`px-6 pb-4 transition-all duration-300 ${openFaq === index ? 'block' : 'hidden'}`}>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 혜택 하이라이트 */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 py-16 mb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">왜 SWITCHUP 인가요?</h2>
            <p className="text-xl text-blue-100">
              간편한 설정부터 강력한 분석까지, 모든 비즈니스를 위한 전환율 최적화 솔루션
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">간편한 설정</h3>
              <p className="text-blue-100">단 한 줄의 코드 추가로 개발자 없이도 누구나 쉽게 테스트를 시작할 수 있습니다.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">정확한 타겟팅</h3>
              <p className="text-blue-100">방문자를 여러 기준으로 세분화하여 대상별로 차별화된 경험을 제공할 수 있습니다.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">통계적 신뢰성</h3>
              <p className="text-blue-100">통계적으로 유의미한 결과를 제공하여 데이터 기반의 확실한 의사결정을 도와드립니다.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA 섹션 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="bg-gray-50 rounded-2xl p-12 text-center relative overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 -z-10 opacity-10">
            <svg width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="200" cy="200" r="200" fill="url(#blue-radial-small)" />
              <defs>
                <radialGradient id="blue-radial-small" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(200 200) rotate(90) scale(200)">
                  <stop stopColor="#3B82F6" />
                  <stop offset="1" stopColor="#3B82F6" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">지금 바로 첫 실험을 시작하세요</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            30일 무료 체험으로 SWITCHUP의 모든 기능을 경험해보세요.
            무료 체험은 문의를 통해 진행하실 수 있습니다.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium">
                무료로 시작하기
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* 결제 및 이용 안내 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">결제 및 이용 안내</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">기본 요금(₩100,000)에는 월 2만 명의 '테스트별 참여자 수'가 포함되어 있습니다.</span>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">추가 참여자는 1명당 1.5원의 요금이 부과됩니다.</span>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">모든 요금제는 부가가치세(VAT)가 별도로 부과됩니다.</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">결제 시 월간 자동 결제로 진행되며, 언제든지 해지 가능합니다.</span>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">
                  환불 정책은 <Link to="/terms" className="text-blue-600 hover:text-blue-800">서비스 이용 약관</Link>에서 확인하실 수 있습니다.
                </span>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-600">
                  현재 표시된 가격은 오픈 이벤트 특별 할인 금액이며, 이벤트 종료 시 별도로 안내드립니다.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 카카오톡 문의 버튼 */}
      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div >
  );
};

export default Pricing;